<template>
  <section class="mitems-panel">
    <div class="mitems-panel__head">
      <div>
        <h2 class="mitems-panel__title">Farmaci personali</h2>
        <p class="mitems-panel__subtitle">
          Anagrafica farmaci e carichi disponibili per questo ospite
        </p>
      </div>
      <button class="villa-btn villa-btn--primary" @click="openCreateItem">
        <i class="fa-solid fa-plus"></i> Nuovo farmaco
      </button>
    </div>

    <div v-if="loading" class="mitems-state">Caricamento farmaci…</div>
    <div v-else-if="error" class="mitems-state">{{ error }}</div>
    <div v-else-if="items.length === 0" class="mitems-state">
      Nessun farmaco registrato. Aggiungi il primo farmaco.
    </div>

    <div v-else class="mitems-table-wrap">
      <table class="guests-table">
        <thead>
          <tr>
            <th>Farmaco</th>
            <th>Confezione</th>
            <th>Disponibilità</th>
            <th>Scadenza</th>
            <th>Stato</th>
            <th class="actions-cell">Azioni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="it in items" :key="it.id">
            <td>
              <div class="mitem-name">{{ it.name }}</div>
              <div v-if="anagSub(it)" class="mitem-sub">{{ anagSub(it) }}</div>
            </td>
            <td>
              <div class="mitem-sub">
                {{ fmtNum(it.dosesPerPackage) }} {{ it.doseUnit }}/conf.
              </div>
              <div class="mitem-sub">{{ it.stockLoadsCount }} carichi</div>
            </td>
            <td class="mitem-avail">
              {{ fmtNum(it.availableDoses) }} {{ it.doseUnit }}
            </td>
            <td>
              <span
                :class="{
                  'mitem-expiry--soon': isExpirySoon(it.nextExpiryDate),
                }"
              >
                {{ formatDate(it.nextExpiryDate) }}
              </span>
            </td>
            <td>
              <span
                :class="['mitem-badge', 'mitem-badge--' + it.calculatedStatus]"
              >
                {{ statusLabel(it.calculatedStatus) }}
              </span>
            </td>
            <td class="actions-cell">
              <button
                class="icon-btn"
                title="Dettaglio e carichi"
                @click="openDetail(it)"
              >
                <i class="fa-solid fa-boxes-stacked"></i>
              </button>
              <button
                class="icon-btn"
                title="Modifica"
                @click="openEditItem(it)"
              >
                <i class="fa-solid fa-pen-to-square"></i>
              </button>
              <button
                class="icon-btn icon-btn--danger"
                title="Archivia"
                @click="askArchiveItem(it)"
              >
                <i class="fa-solid fa-box-archive"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modale anagrafica farmaco (crea/modifica) -->
    <VueFinalModal v-model="itemModal.open" content-class="villa-modal">
      <h3 class="villa-modal__title">
        {{ itemModal.mode === "edit" ? "Modifica farmaco" : "Nuovo farmaco" }}
      </h3>
      <div class="form-grid">
        <div class="form-field form-field--full mitem-ac">
          <label>Nome farmaco <span class="required">*</span></label>
          <input
            v-model.trim="itemForm.name"
            type="text"
            autocomplete="off"
            placeholder="Digita per cercare nel catalogo AIFA o inserisci a mano"
            @input="onNameInput($event)"
            @focus="onNameFocus"
            @blur="onNameBlur"
          />
          <span v-if="itemForm.source === 'aifa'" class="mitem-ac__tag">
            <i class="fa-solid fa-circle-check"></i> da catalogo AIFA
            <template v-if="itemForm.aicCode"
              >· AIC {{ itemForm.aicCode }}</template
            >
          </span>
          <div v-if="ac.open" class="mitem-ac__dropdown">
            <div v-if="ac.loading" class="mitem-ac__state">
              Ricerca nel catalogo AIFA…
            </div>
            <div v-else-if="!ac.results.length" class="mitem-ac__state">
              Nessun farmaco AIFA per “{{ ac.query }}”. Puoi inserirlo
              manualmente.
            </div>
            <ul v-else class="mitem-ac__list">
              <li
                v-for="r in ac.results"
                :key="r.id"
                class="mitem-ac__item"
                @mousedown.prevent="selectCatalog(r)"
              >
                <div class="mitem-ac__name">{{ r.name }}</div>
                <div class="mitem-ac__meta">
                  <span v-if="r.activeIngredientsText">
                    {{ r.activeIngredientsText }}
                  </span>
                  <span v-if="r.pharmaceuticalForm">
                    · {{ r.pharmaceuticalForm }}
                  </span>
                  <span v-if="r.companyName"> · {{ r.companyName }}</span>
                  <span v-if="r.aicCode"> · AIC {{ r.aicCode }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div class="form-field">
          <label>Principio attivo</label>
          <input v-model.trim="itemForm.activeIngredient" type="text" />
        </div>
        <div class="form-field">
          <label>Dosaggio / concentrazione</label>
          <input
            v-model.trim="itemForm.strength"
            type="text"
            placeholder="es. 500mg"
          />
        </div>
        <div class="form-field">
          <label>Forma farmaceutica</label>
          <input
            v-model.trim="itemForm.pharmaceuticalForm"
            type="text"
            placeholder="es. compressa"
          />
        </div>
        <div class="form-field">
          <label>Descrizione confezione</label>
          <input
            v-model.trim="itemForm.packageDescription"
            type="text"
            placeholder="es. blister 30 cpr"
          />
        </div>
        <div class="form-field">
          <label>Dosi per confezione</label>
          <input
            v-model="itemForm.dosesPerPackage"
            type="number"
            min="0"
            step="any"
          />
        </div>
        <div class="form-field">
          <label>Unità dose</label>
          <input
            v-model.trim="itemForm.doseUnit"
            type="text"
            placeholder="es. compresse"
          />
        </div>
        <div class="form-field form-field--full">
          <label>Note</label>
          <textarea v-model.trim="itemForm.notes" rows="2"></textarea>
        </div>
      </div>
      <div class="form-actions">
        <button
          class="villa-btn villa-btn--ghost"
          @click="itemModal.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="itemModal.busy"
          @click="submitItem"
        >
          {{
            itemModal.busy
              ? "Salvataggio…"
              : itemModal.mode === "edit"
              ? "Salva"
              : "Crea"
          }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Modale dettaglio farmaco + carichi -->
    <VueFinalModal
      v-model="detail.open"
      content-class="villa-modal villa-modal--wide"
    >
      <h3 class="villa-modal__title">
        {{ detail.item ? detail.item.name : "Dettaglio farmaco" }}
      </h3>

      <div v-if="detail.loading" class="mitems-state">Caricamento…</div>
      <template v-else-if="detail.item">
        <div class="mitem-detail__grid">
          <div class="mitem-detail__item">
            <span class="mitem-detail__label">Principio attivo</span>
            <span class="mitem-detail__value">
              {{ detail.item.activeIngredient || "—" }}
            </span>
          </div>
          <div class="mitem-detail__item">
            <span class="mitem-detail__label">Dosaggio</span>
            <span class="mitem-detail__value">
              {{ detail.item.strength || "—" }}
            </span>
          </div>
          <div class="mitem-detail__item">
            <span class="mitem-detail__label">Forma</span>
            <span class="mitem-detail__value">
              {{ detail.item.pharmaceuticalForm || "—" }}
            </span>
          </div>
          <div class="mitem-detail__item">
            <span class="mitem-detail__label">Confezione</span>
            <span class="mitem-detail__value">
              {{ fmtNum(detail.item.dosesPerPackage) }}
              {{ detail.item.doseUnit }}
            </span>
          </div>
          <div class="mitem-detail__item">
            <span class="mitem-detail__label">Disponibilità</span>
            <span class="mitem-detail__value">
              <strong>{{ fmtNum(detail.item.availableDoses) }}</strong>
              {{ detail.item.doseUnit }}
            </span>
          </div>
          <div class="mitem-detail__item">
            <span class="mitem-detail__label">Stato</span>
            <span class="mitem-detail__value">
              <span
                :class="[
                  'mitem-badge',
                  'mitem-badge--' + detail.item.calculatedStatus,
                ]"
              >
                {{ statusLabel(detail.item.calculatedStatus) }}
              </span>
            </span>
          </div>
        </div>

        <div class="mitem-loads__head">
          <h4 class="mitem-loads__title">Carichi stock</h4>
          <button class="villa-btn villa-btn--primary" @click="openCreateLoad">
            <i class="fa-solid fa-plus"></i> Nuovo carico
          </button>
        </div>

        <div v-if="detail.loads.length === 0" class="mitems-state">
          Nessun carico. Aggiungi il primo carico.
        </div>
        <div v-else class="mitems-table-wrap">
          <table class="guests-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Conf.</th>
                <th>Totale</th>
                <th>Residue</th>
                <th>Lotto</th>
                <th>Scadenza</th>
                <th class="actions-cell">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ld in detail.loads" :key="ld.id">
                <td>{{ formatDate(ld.loadedAt) }}</td>
                <td>{{ fmtNum(ld.packagesCount) }}</td>
                <td>{{ fmtNum(ld.totalDoses) }}</td>
                <td>{{ fmtNum(ld.remainingDoses) }}</td>
                <td>{{ ld.lotNumber || "—" }}</td>
                <td>
                  <span
                    :class="{
                      'mitem-expiry--soon': isExpirySoon(ld.expiryDate),
                    }"
                  >
                    {{ formatDate(ld.expiryDate) }}
                  </span>
                </td>
                <td class="actions-cell">
                  <button
                    class="icon-btn"
                    title="Modifica"
                    @click="openEditLoad(ld)"
                  >
                    <i class="fa-solid fa-pen-to-square"></i>
                  </button>
                  <button
                    class="icon-btn icon-btn--danger"
                    title="Archivia"
                    @click="askArchiveLoad(ld)"
                  >
                    <i class="fa-solid fa-box-archive"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <div class="form-actions">
        <button class="villa-btn villa-btn--ghost" @click="detail.open = false">
          Chiudi
        </button>
      </div>
    </VueFinalModal>

    <!-- Modale carico (crea/modifica) -->
    <VueFinalModal v-model="loadModal.open" content-class="villa-modal">
      <h3 class="villa-modal__title">
        {{ loadModal.mode === "edit" ? "Modifica carico" : "Nuovo carico" }}
      </h3>
      <div class="form-grid">
        <div class="form-field">
          <label>Numero confezioni</label>
          <input
            v-model="loadForm.packagesCount"
            type="number"
            min="0"
            step="any"
          />
        </div>
        <div class="form-field">
          <label>Dosi per confezione</label>
          <input
            v-model="loadForm.dosesPerPackageSnapshot"
            type="number"
            min="0"
            step="any"
            :placeholder="snapshotPlaceholder"
          />
        </div>
        <div class="form-field">
          <label>Totale dosi</label>
          <input
            v-model="loadForm.totalDoses"
            type="number"
            min="0"
            step="any"
            placeholder="auto"
          />
        </div>
        <div class="form-field">
          <label>Dosi residue</label>
          <input
            v-model="loadForm.remainingDoses"
            type="number"
            min="0"
            step="any"
            placeholder="auto"
          />
        </div>
        <div class="form-field">
          <label>Lotto</label>
          <input v-model.trim="loadForm.lotNumber" type="text" />
        </div>
        <div class="form-field">
          <label>Scadenza</label>
          <input v-model="loadForm.expiryDate" type="date" />
        </div>
        <div class="form-field">
          <label>Data carico</label>
          <input v-model="loadForm.loadedAt" type="date" />
        </div>
        <div class="form-field form-field--full">
          <label>Note</label>
          <textarea v-model.trim="loadForm.notes" rows="2"></textarea>
        </div>
      </div>
      <p class="load-preview">{{ loadPreview }}</p>
      <div class="form-actions">
        <button
          class="villa-btn villa-btn--ghost"
          @click="loadModal.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="loadModal.busy"
          @click="submitLoad"
        >
          {{
            loadModal.busy
              ? "Salvataggio…"
              : loadModal.mode === "edit"
              ? "Salva"
              : "Crea"
          }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Conferma archiviazione (farmaco o carico) -->
    <VueFinalModal v-model="confirm.open" content-class="villa-confirm">
      <h3 class="villa-confirm__title">
        Archivia {{ confirm.kind === "item" ? "farmaco" : "carico" }}
      </h3>
      <p class="villa-confirm__text">
        Vuoi archiviare <strong>{{ confirm.label }}</strong
        >? Non sarà più visibile in elenco.
      </p>
      <div class="villa-confirm__actions">
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="confirm.busy"
          @click="confirm.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--danger"
          :disabled="confirm.busy"
          @click="confirmArchive"
        >
          {{ confirm.busy ? "Archivia…" : "Archivia" }}
        </button>
      </div>
    </VueFinalModal>
  </section>
</template>

<script>
import { mapActions } from "vuex";

const STATUSES = {
  available: "Disponibile",
  low_stock: "Scorta bassa",
  exhausted: "Esaurito",
  expired: "Scaduto",
};

const emptyItemForm = () => ({
  name: "",
  activeIngredient: "",
  strength: "",
  pharmaceuticalForm: "",
  packageDescription: "",
  dosesPerPackage: "0",
  doseUnit: "unità",
  notes: "",
  // Collegamento opzionale al catalogo AIFA (D11).
  catalogItemId: null,
  aicCode: null,
  source: "custom",
});

const emptyAutocomplete = () => ({
  open: false,
  loading: false,
  query: "",
  results: [],
  timer: null,
});

const emptyLoadForm = () => ({
  packagesCount: "1",
  dosesPerPackageSnapshot: "",
  totalDoses: "",
  remainingDoses: "",
  lotNumber: "",
  expiryDate: "",
  loadedAt: "",
  notes: "",
});

export default {
  name: "GuestMedicationItemsPanel",
  props: {
    guestId: { type: [String, Number], required: true },
  },
  data() {
    return {
      items: [],
      loading: true,
      error: "",
      itemForm: emptyItemForm(),
      ac: emptyAutocomplete(),
      itemModal: { open: false, mode: "create", busy: false, id: null },
      detail: { open: false, loading: false, item: null, loads: [] },
      loadForm: emptyLoadForm(),
      loadModal: { open: false, mode: "create", busy: false, id: null },
      confirm: { open: false, kind: "item", busy: false, id: null, label: "" },
    };
  },
  computed: {
    snapshotPlaceholder() {
      const dpp = this.detail.item
        ? Number(this.detail.item.dosesPerPackage)
        : 0;
      return `default: ${this.fmtNum(dpp)}`;
    },
    loadPreview() {
      const pkg =
        this.loadForm.packagesCount === ""
          ? 1
          : Number(this.loadForm.packagesCount);
      const snapDefault = this.detail.item
        ? Number(this.detail.item.dosesPerPackage || 0)
        : 0;
      const snap =
        this.loadForm.dosesPerPackageSnapshot === ""
          ? snapDefault
          : Number(this.loadForm.dosesPerPackageSnapshot);
      const total =
        this.loadForm.totalDoses === ""
          ? (Number.isFinite(pkg) ? pkg : 0) *
            (Number.isFinite(snap) ? snap : 0)
          : Number(this.loadForm.totalDoses);
      const remaining =
        this.loadForm.remainingDoses === ""
          ? total
          : Number(this.loadForm.remainingDoses);
      if (!Number.isFinite(total) || !Number.isFinite(remaining)) return "";
      return `Anteprima: totale ${this.fmtNum(total)} · residue ${this.fmtNum(
        remaining
      )} (il calcolo definitivo è del backend)`;
    },
  },
  methods: {
    ...mapActions([
      "fetchGuestMedicationItems",
      "fetchGuestMedicationItem",
      "createGuestMedicationItem",
      "updateGuestMedicationItem",
      "archiveGuestMedicationItem",
      "fetchGuestMedicationStockLoads",
      "createGuestMedicationStockLoad",
      "updateGuestMedicationStockLoad",
      "archiveGuestMedicationStockLoad",
      "searchMedicationCatalog",
    ]),

    // --- Autocomplete catalogo AIFA (D11) ---
    // Solo supporto al data-entry: l'utente può sempre digitare a mano e non è
    // obbligato a scegliere dal catalogo. Niente suggerimenti terapeutici.
    onNameInput(e) {
      const value = e && e.target ? e.target.value : this.itemForm.name;
      // Modificando il nome a mano si scollega da un'eventuale voce AIFA.
      if (this.itemForm.source === "aifa") {
        this.itemForm.source = "custom";
        this.itemForm.catalogItemId = null;
        this.itemForm.aicCode = null;
      }
      this.scheduleCatalogSearch(value);
    },
    scheduleCatalogSearch(value) {
      const q = (value || "").trim();
      this.ac.query = q;
      if (this.ac.timer) clearTimeout(this.ac.timer);
      if (q.length < 2) {
        this.ac.open = false;
        this.ac.loading = false;
        this.ac.results = [];
        return;
      }
      this.ac.open = true;
      this.ac.loading = true;
      this.ac.timer = setTimeout(() => this.runCatalogSearch(q), 250);
    },
    async runCatalogSearch(q) {
      try {
        const results = await this.searchMedicationCatalog({ q, limit: 20 });
        // Evita race condition: applica solo se la query è ancora attuale.
        if (this.ac.query !== q) return;
        this.ac.results = results;
      } catch {
        if (this.ac.query === q) this.ac.results = [];
      } finally {
        if (this.ac.query === q) this.ac.loading = false;
      }
    },
    selectCatalog(r) {
      this.itemForm.name = r.name || "";
      if (r.pharmaceuticalForm) {
        this.itemForm.pharmaceuticalForm = r.pharmaceuticalForm;
      }
      if (r.activeIngredientsText && !this.itemForm.activeIngredient) {
        this.itemForm.activeIngredient = r.activeIngredientsText;
      }
      this.itemForm.catalogItemId = r.id;
      this.itemForm.aicCode = r.aicCode || null;
      this.itemForm.source = "aifa";
      this.ac.open = false;
      this.ac.results = [];
      this.ac.query = this.itemForm.name;
    },
    onNameFocus() {
      if (this.ac.query.length >= 2 && this.ac.results.length) {
        this.ac.open = true;
      }
    },
    onNameBlur() {
      // Ritardo: lascia completare il click (mousedown) sulla voce.
      setTimeout(() => {
        this.ac.open = false;
      }, 150);
    },

    statusLabel(s) {
      return STATUSES[s] || s;
    },
    fmtNum(n) {
      const v = Number(n);
      return Number.isFinite(v) ? String(v) : "0";
    },
    anagSub(it) {
      return [it.activeIngredient, it.strength, it.pharmaceuticalForm]
        .filter(Boolean)
        .join(" · ");
    },
    formatDate(d) {
      if (!d) return "—";
      const [y, m, day] = String(d).slice(0, 10).split("-");
      return day && m && y ? `${day}/${m}/${y}` : d;
    },
    isExpirySoon(d) {
      if (!d) return false;
      const exp = new Date(String(d).slice(0, 10));
      if (Number.isNaN(exp.getTime())) return false;
      const in30 = new Date();
      in30.setDate(in30.getDate() + 30);
      return exp <= in30;
    },

    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.items = await this.fetchGuestMedicationItems(this.guestId);
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Errore nel caricamento dei farmaci.";
      } finally {
        this.loading = false;
      }
    },

    // --- Anagrafica farmaco ---
    openCreateItem() {
      this.itemForm = emptyItemForm();
      this.ac = emptyAutocomplete();
      this.itemModal = { open: true, mode: "create", busy: false, id: null };
    },
    openEditItem(it) {
      this.itemForm = {
        name: it.name || "",
        activeIngredient: it.activeIngredient || "",
        strength: it.strength || "",
        pharmaceuticalForm: it.pharmaceuticalForm || "",
        packageDescription: it.packageDescription || "",
        dosesPerPackage:
          it.dosesPerPackage != null ? String(it.dosesPerPackage) : "0",
        doseUnit: it.doseUnit || "unità",
        notes: it.notes || "",
        catalogItemId: it.catalogItemId != null ? it.catalogItemId : null,
        aicCode: it.aicCode || null,
        source: it.source || "custom",
      };
      this.ac = emptyAutocomplete();
      this.itemModal = { open: true, mode: "edit", busy: false, id: it.id };
    },
    buildItemPayload() {
      const dpp =
        this.itemForm.dosesPerPackage === ""
          ? 0
          : Number(this.itemForm.dosesPerPackage);
      return {
        name: this.itemForm.name,
        activeIngredient: this.itemForm.activeIngredient || null,
        strength: this.itemForm.strength || null,
        pharmaceuticalForm: this.itemForm.pharmaceuticalForm || null,
        packageDescription: this.itemForm.packageDescription || null,
        dosesPerPackage: dpp,
        doseUnit: this.itemForm.doseUnit || "unità",
        notes: this.itemForm.notes || null,
        // Collegamento opzionale al catalogo AIFA (D11).
        catalogItemId: this.itemForm.catalogItemId || null,
        aicCode: this.itemForm.aicCode || null,
        source: this.itemForm.source || "custom",
      };
    },
    async submitItem() {
      if (!this.itemForm.name) {
        return this.$toast.error("Il nome del farmaco è obbligatorio.");
      }
      const dpp =
        this.itemForm.dosesPerPackage === ""
          ? 0
          : Number(this.itemForm.dosesPerPackage);
      if (!Number.isFinite(dpp) || dpp < 0) {
        return this.$toast.error(
          "Le dosi per confezione devono essere un numero maggiore o uguale a 0."
        );
      }
      this.itemModal.busy = true;
      try {
        const payload = this.buildItemPayload();
        if (this.itemModal.mode === "edit") {
          await this.updateGuestMedicationItem({
            guestId: this.guestId,
            itemId: this.itemModal.id,
            payload,
          });
          this.$toast.success("Farmaco aggiornato.");
        } else {
          await this.createGuestMedicationItem({
            guestId: this.guestId,
            payload,
          });
          this.$toast.success("Farmaco aggiunto.");
        }
        this.itemModal.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Salvataggio farmaco non riuscito."
        );
      } finally {
        this.itemModal.busy = false;
      }
    },
    askArchiveItem(it) {
      this.confirm = {
        open: true,
        kind: "item",
        busy: false,
        id: it.id,
        label: it.name,
      };
    },

    // --- Dettaglio + carichi ---
    async openDetail(it) {
      this.detail = { open: true, loading: true, item: null, loads: [] };
      await this.refreshDetail(it.id);
    },
    async refreshDetail(itemId) {
      this.detail.loading = true;
      try {
        const [item, loads] = await Promise.all([
          this.fetchGuestMedicationItem({ guestId: this.guestId, itemId }),
          this.fetchGuestMedicationStockLoads({
            guestId: this.guestId,
            itemId,
          }),
        ]);
        this.detail.item = item;
        this.detail.loads = loads;
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Errore nel caricamento del dettaglio."
        );
        this.detail.open = false;
      } finally {
        this.detail.loading = false;
      }
    },

    // --- Carichi ---
    openCreateLoad() {
      this.loadForm = emptyLoadForm();
      this.loadModal = { open: true, mode: "create", busy: false, id: null };
    },
    openEditLoad(ld) {
      this.loadForm = {
        packagesCount: ld.packagesCount != null ? String(ld.packagesCount) : "",
        dosesPerPackageSnapshot:
          ld.dosesPerPackageSnapshot != null
            ? String(ld.dosesPerPackageSnapshot)
            : "",
        totalDoses: ld.totalDoses != null ? String(ld.totalDoses) : "",
        remainingDoses:
          ld.remainingDoses != null ? String(ld.remainingDoses) : "",
        lotNumber: ld.lotNumber || "",
        expiryDate: ld.expiryDate ? String(ld.expiryDate).slice(0, 10) : "",
        loadedAt: ld.loadedAt ? String(ld.loadedAt).slice(0, 10) : "",
        notes: ld.notes || "",
      };
      this.loadModal = { open: true, mode: "edit", busy: false, id: ld.id };
    },
    buildLoadPayload() {
      const p = {};
      const numIf = (key, val) => {
        if (val !== "" && val != null) p[key] = Number(val);
      };
      numIf("packagesCount", this.loadForm.packagesCount);
      numIf("dosesPerPackageSnapshot", this.loadForm.dosesPerPackageSnapshot);
      numIf("totalDoses", this.loadForm.totalDoses);
      numIf("remainingDoses", this.loadForm.remainingDoses);
      if (this.loadForm.lotNumber) p.lotNumber = this.loadForm.lotNumber;
      if (this.loadForm.expiryDate) p.expiryDate = this.loadForm.expiryDate;
      if (this.loadForm.loadedAt) p.loadedAt = this.loadForm.loadedAt;
      if (this.loadForm.notes) p.notes = this.loadForm.notes;
      return p;
    },
    validateLoad(payload) {
      for (const k of [
        "packagesCount",
        "dosesPerPackageSnapshot",
        "totalDoses",
        "remainingDoses",
      ]) {
        if (k in payload && (!Number.isFinite(payload[k]) || payload[k] < 0)) {
          return "I valori numerici devono essere maggiori o uguali a 0.";
        }
      }
      if (
        "remainingDoses" in payload &&
        "totalDoses" in payload &&
        payload.remainingDoses > payload.totalDoses
      ) {
        return "Le dosi residue non possono superare il totale dosi.";
      }
      return "";
    },
    async submitLoad() {
      const payload = this.buildLoadPayload();
      const validationError = this.validateLoad(payload);
      if (validationError) return this.$toast.error(validationError);

      this.loadModal.busy = true;
      try {
        if (this.loadModal.mode === "edit") {
          await this.updateGuestMedicationStockLoad({
            guestId: this.guestId,
            itemId: this.detail.item.id,
            loadId: this.loadModal.id,
            payload,
          });
          this.$toast.success("Carico aggiornato.");
        } else {
          await this.createGuestMedicationStockLoad({
            guestId: this.guestId,
            itemId: this.detail.item.id,
            payload,
          });
          this.$toast.success("Carico registrato.");
        }
        this.loadModal.open = false;
        await this.refreshDetail(this.detail.item.id);
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Salvataggio carico non riuscito."
        );
      } finally {
        this.loadModal.busy = false;
      }
    },
    askArchiveLoad(ld) {
      this.confirm = {
        open: true,
        kind: "load",
        busy: false,
        id: ld.id,
        label: `carico del ${this.formatDate(ld.loadedAt)}`,
      };
    },

    // --- Archiviazione condivisa ---
    async confirmArchive() {
      this.confirm.busy = true;
      try {
        if (this.confirm.kind === "item") {
          await this.archiveGuestMedicationItem({
            guestId: this.guestId,
            itemId: this.confirm.id,
          });
          this.$toast.success("Farmaco archiviato.");
          this.confirm.open = false;
          await this.load();
        } else {
          await this.archiveGuestMedicationStockLoad({
            guestId: this.guestId,
            itemId: this.detail.item.id,
            loadId: this.confirm.id,
          });
          this.$toast.success("Carico archiviato.");
          this.confirm.open = false;
          await this.refreshDetail(this.detail.item.id);
          await this.load();
        }
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Archiviazione non riuscita."
        );
      } finally {
        this.confirm.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/GuestMedicationItemsPanel.css" />
<style scoped>
/* Autocomplete catalogo AIFA (D11) */
.mitem-ac {
  position: relative;
}
.mitem-ac__tag {
  margin-top: 4px;
  font-size: 12px;
  color: #15803d;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.mitem-ac__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 50;
  margin-top: 4px;
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(8, 43, 87, 0.16);
  max-height: 280px;
  overflow-y: auto;
}
.mitem-ac__state {
  padding: 12px 14px;
  font-size: 13px;
  color: var(--villa-text-muted, #64748b);
}
.mitem-ac__list {
  list-style: none;
  margin: 0;
  padding: 4px;
}
.mitem-ac__item {
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
}
.mitem-ac__item:hover {
  background: var(--villa-bg, #f8fbff);
}
.mitem-ac__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--villa-navy, #082b57);
}
.mitem-ac__meta {
  font-size: 12px;
  color: var(--villa-text-muted, #64748b);
  margin-top: 2px;
}
</style>
